export type SlideHistoryStacks<T> = {
  undo: T[];
  redo: T[];
};

export type SlideHistoryEntry<T> = {
  slideId: string;
  objects: T;
};

export function emptySlideHistory<T>(): SlideHistoryStacks<T> {
  return { undo: [], redo: [] };
}

export function pushSlideHistory<T>(
  stacks: Map<string, SlideHistoryStacks<T>>,
  slideId: string,
  objects: T,
  limit = 30,
): Map<string, SlideHistoryStacks<T>> {
  const current = stacks.get(slideId) || emptySlideHistory<T>();
  stacks.set(slideId, {
    undo: [...current.undo, objects].slice(-limit),
    redo: [],
  });
  return stacks;
}

export function popSlideUndo<T>(
  stacks: Map<string, SlideHistoryStacks<T>>,
  slideId: string,
  currentObjects: T,
): { next: T; stacks: Map<string, SlideHistoryStacks<T>> } | null {
  const current = stacks.get(slideId);
  if (!current?.undo.length) return null;
  const previous = current.undo[current.undo.length - 1];
  stacks.set(slideId, {
    undo: current.undo.slice(0, -1),
    redo: [...current.redo, currentObjects],
  });
  return { next: previous, stacks };
}

export function popSlideRedo<T>(
  stacks: Map<string, SlideHistoryStacks<T>>,
  slideId: string,
  currentObjects: T,
): { next: T; stacks: Map<string, SlideHistoryStacks<T>> } | null {
  const current = stacks.get(slideId);
  if (!current?.redo.length) return null;
  const next = current.redo[current.redo.length - 1];
  stacks.set(slideId, {
    undo: [...current.undo, currentObjects],
    redo: current.redo.slice(0, -1),
  });
  return { next, stacks };
}

export function canUndo<T>(stacks: Map<string, SlideHistoryStacks<T>>, slideId: string | null): boolean {
  if (!slideId) return false;
  return Boolean(stacks.get(slideId)?.undo.length);
}

export function canRedo<T>(stacks: Map<string, SlideHistoryStacks<T>>, slideId: string | null): boolean {
  if (!slideId) return false;
  return Boolean(stacks.get(slideId)?.redo.length);
}

export function applyOnlyIfSameSlide<T>(entry: SlideHistoryEntry<T> | null, activeSlideId: string | null): T | null {
  if (!entry || !activeSlideId || entry.slideId !== activeSlideId) return null;
  return entry.objects;
}
